import React from 'react';

import { useController, useFormContext } from 'react-hook-form';
import { FormControl, FormHelperText } from '@mui/material';

import { Link, RadioGroup } from '@infoods/lifebase-ui';

import classes from './ScoringRulesStep.module.scss';
import { ChallengeFormValues } from '../../../../../../interfaces/ChallengeFormValues';
import { STEP_FORM_ID } from '../../GuidedChallengeCreation.settings';

import { CHALLENGE_CUSTOMER_TYPE } from '../../../../../../constants/challengeCustomerType';
import { getHumanizeChallengeCustomerType } from '../../../../../../utils/getHumanizeChallengeCustomerType/getHumanizeChallengeCustomerType';
import { useFormDependentOptions } from '../../../../../../../shared/hooks/useFormDependentOptions';
import { getScoringRuleOptionsForCustomerType } from '../../../../../../utils/getScoringRuleOptionsForCustomerType/getScoringRuleOptionsForCustomerType';
import { CustomerTypeRadioRow } from './CustomerTypeRadioRow/CustomerTypeRadioRow';
import { SolutionTypeFlags } from '../../../../../../../shared/constants/user-info/solutionType';

type Props = {
  onNext: () => void;
  availableSolutionsFlags: SolutionTypeFlags;
};

const ScoringRulesStep = ({ onNext, availableSolutionsFlags }: Props) => {
  const { control, trigger, resetField } = useFormContext<ChallengeFormValues>();
  const isLifeBaseUsersAvailable = availableSolutionsFlags.NUTRITION;

  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name: 'about.customerType',
    rules: { required: 'Select customer type is required' },
  });

  const selectedCustomerType = field.value;

  const { availableOptions: availableScoringRuleOptions } = useFormDependentOptions({
    control,
    parentName: 'about.customerType',
    childName: 'about.scoringRules',
    getOptions: getScoringRuleOptionsForCustomerType,
    onResetField: (name) => {
      resetField(name, { defaultValue: '' });
    },
  });

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isValid = await trigger(['about.customerType', 'about.scoringRules']);

    if (isValid) {
      onNext();
    }
  };

  return (
    <form id={STEP_FORM_ID} onSubmit={onSubmit} noValidate>
      <FormControl error={!!error}>
        <RadioGroup {...field} className={classes.radioGroup}>
          <CustomerTypeRadioRow
            control={control}
            selectedValue={selectedCustomerType}
            value={CHALLENGE_CUSTOMER_TYPE.LIFE_BASE_USERS}
            availableScoringRuleOptions={availableScoringRuleOptions}
            title={getHumanizeChallengeCustomerType(
              CHALLENGE_CUSTOMER_TYPE.LIFE_BASE_USERS,
            )}
            description={
              <span>
                Includes food tracking. Requires the SWYMO nutrition platform. To learn
                more, visit:&nbsp;
                <Link href="https://lifebasesolutions.com" target="_blank">
                  lifebasesolutions.com
                </Link>
              </span>
            }
            disabled={!isLifeBaseUsersAvailable}
          />
          <CustomerTypeRadioRow
            value={CHALLENGE_CUSTOMER_TYPE.OPEN_TO_EVERYONE}
            selectedValue={selectedCustomerType}
            control={control}
            availableScoringRuleOptions={availableScoringRuleOptions}
            title={getHumanizeChallengeCustomerType(
              CHALLENGE_CUSTOMER_TYPE.OPEN_TO_EVERYONE,
            )}
            description="Food tracking is not included."
          />
        </RadioGroup>
        {error?.message && <FormHelperText>{error?.message}</FormHelperText>}
      </FormControl>
    </form>
  );
};

export { ScoringRulesStep };
